import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import CreditCardRoundedIcon from '@mui/icons-material/CreditCardRounded';
import requests, { ApiError, extractApiMessage } from '../../api/client';
import { useSession } from '../../Session';
import { useFeedback } from '../common/Feedback';
import { useTranslation } from '../../i18n';
import { cardSx, colors, primaryButtonSx, radius } from '../../theme/tokens';
import { extractDocument } from '../../utils/document';
import { onlyDigits, samePhone } from '../../utils/phone';
import { findPlan } from '../../utils/plans';
import { ROUTES } from '../../utils/routes';
import { executeRecaptcha, isRecaptchaConfigured } from '../../utils/recaptcha';
import {
  detectCardBrand,
  formatCardNumber,
  formatCvv,
  formatExpiry,
  validateCardForm,
  type CardField,
  type CardValidationIssue,
} from '../../utils/card';
import { OrderSummary } from './OrderSummary';
import type { Plan } from '../../types/api';

/**
 * Checkout com cartão de crédito — equivalente a `cartao_credito.blade.php`
 * mais `PlanoController::creditCardView` / `processCreditCardPayment`.
 *
 * A tela recebe `id_plano` e `fone` pela querystring, exatamente como a rota
 * `/{marca}/cartao-credito?id_plano=…&fone=…` do legado, mas **a querystring
 * não é fonte da verdade**: ela só é aceita quando confere com a sessão da
 * linha validada por OTP. As quatro guardas do controller foram mantidas uma a
 * uma (ver `guard` abaixo) — afrouxar qualquer uma reabre a porta para recarga
 * em linha de terceiro.
 *
 * O pagamento continua sendo DUAS chamadas na ordem: tokenizar o cartão (a API
 * é quem guarda o PAN, o SPA não persiste nada) e então solicitar a recarga com
 * o token. O `linha_token` emitido no OTP vai junto: é ele, e não a sessão do
 * navegador, que autoriza a operação do lado do servidor.
 */

/** Motivos de bloqueio; todos terminam em redirect para a home da marca. */
type GuardFailure = 'no-session' | 'missing-params' | 'phone-mismatch' | 'plan-not-found';

type GuardResult =
  | { ok: true; plan: Plan; holderDocument: string | null }
  | { ok: false; reason: GuardFailure };

export function CreditCardCheckout() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { session, clearSession } = useSession();
  const { show } = useFeedback();

  const planParam = searchParams.get('id_plano');
  const phoneParam = searchParams.get('fone');

  const [holder, setHolder] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvv, setCvv] = useState('');
  /** Recorrência vem marcada por padrão, como o `checked` do input legado. */
  const [recurrence, setRecurrence] = useState(true);
  const [issue, setIssue] = useState<CardValidationIssue | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const brand = useMemo(() => detectCardBrand(cardNumber), [cardNumber]);

  /**
   * Sem chave de reCAPTCHA no build não há como obter o token que a rota de
   * cartão exige. Em vez de deixar o cliente preencher o cartão para receber
   * um erro no fim, o formulário já avisa e o botão fica desabilitado.
   */
  const cardPaymentAvailable = isRecaptchaConfigured();

  /**
   * Trocar um Amex (CVV de 4 dígitos) por uma bandeira de 3 deixaria o quarto
   * dígito preso no estado: o `maxLength` do campo diminui, mas o valor já
   * digitado continua lá e o formulário acusaria "CVV inválido" sem que o
   * cliente entendesse o motivo. Aqui o excedente é cortado junto com a troca.
   */
  useEffect(() => {
    setCvv((current) => (current.length > brand.cvvLength ? current.slice(0, brand.cvvLength) : current));
  }, [brand.cvvLength]);

  /**
   * Guardas de integridade, na mesma ordem do `creditCardView`:
   * 1. sessão e planos presentes;
   * 2. `id_plano` e `fone` na URL;
   * 3. `fone` da URL igual ao da linha validada;
   * 4. `id_plano` da URL presente nos planos daquela sessão.
   */
  const guard = useMemo<GuardResult>(() => {
    if (!session || !session.planos || session.planos.length === 0) {
      return { ok: false, reason: 'no-session' };
    }
    if (!planParam || !phoneParam) {
      return { ok: false, reason: 'missing-params' };
    }
    if (!samePhone(phoneParam, session.linha)) {
      return { ok: false, reason: 'phone-mismatch' };
    }

    const plan = findPlan(session.planos, planParam);
    if (!plan) {
      return { ok: false, reason: 'plan-not-found' };
    }

    // O documento do titular SEMPRE vem da resposta da API guardada na sessão.
    // O legado tinha um input escondido só para transportá-lo; aqui ele nem
    // chega a existir como campo de formulário.
    return { ok: true, plan, holderDocument: extractDocument(session.detalhes) };
  }, [session, planParam, phoneParam]);

  /** Mesmos alertas que o controller gravava com `Log::warning`. */
  useEffect(() => {
    if (guard.ok) return;
    if (guard.reason === 'phone-mismatch') {
      console.warn(
        `Security Alert: CreditCard URL Fone [${onlyDigits(phoneParam ?? '')}] mismatch Session Fone [${
          session?.linha ?? ''
        }]`
      );
    }
    if (guard.reason === 'plan-not-found') {
      console.warn(`Security Alert: CreditCard URL Plan ID [${planParam}] not found in Session Plans.`);
    }
  }, [guard, phoneParam, planParam, session]);

  const errorFor = useCallback(
    (field: CardField): string | null =>
      issue && issue.field === field ? t.creditCard.errors[issue.code] : null,
    [issue, t]
  );

  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      if (!guard.ok || submitting || !session) return;

      // A prova de OTP autoriza tanto a tokenização quanto a recarga. Sem ela
      // as duas chamadas seriam recusadas pela API; mandar refazer a
      // verificação é mais claro para o cliente do que um erro de gateway.
      if (!session.linhaToken || !session.otpSessionToken) {
        show({ kind: 'error', title: t.common.error, text: t.api.sessionExpired });
        return;
      }

      if (!cardPaymentAvailable) {
        show({ kind: 'error', title: t.common.error, text: t.creditCard.errors.recaptchaUnavailable });
        return;
      }

      const found = validateCardForm({ holder, number: cardNumber, expiry, cvv });
      if (found) {
        setIssue(found);
        return;
      }
      setIssue(null);
      setSubmitting(true);

      try {
        // Passo 1 — tokenização. O PAN sai daqui direto para a API PCI e não é
        // guardado, logado nem colocado em storage em nenhum momento.
        // O documento NÃO vai no corpo: a API o resolve no servidor a partir
        // da linha vinculada ao `otp_session_token`. É assim que a checagem
        // "o documento é o do dono da linha validada" — que o Laravel fazia
        // consultando a sessão de servidor — sobrevive à conversão.
        const tokenResponse = await requests.tokenizeCard({
          otp_session_token: session.otpSessionToken,
          nome_titular: holder.trim().toUpperCase(),
          numero_cartao: onlyDigits(cardNumber),
          mes_ano_vencimento: formatExpiry(expiry),
          cvv: onlyDigits(cvv),
        });

        // A API já devolveu o token nos dois formatos ao longo do tempo
        // (`dados.token` e `token` na raiz); o legado aceitava ambos.
        const cardToken = tokenResponse?.dados?.token || tokenResponse?.token || null;
        if (!cardToken) {
          throw new Error(extractApiMessage(tokenResponse, t.creditCard.errors.failed));
        }

        // A API pede o IP do cliente na recarga com cartão (é o que alimenta a
        // análise antifraude). O backend Laravel usava `$request->ip()`; num SPA
        // o IP precisa ser consultado. Falhar aqui não impede o pagamento.
        const clientIp = await requests.currentIp();

        // A rota de cartão exige o reCAPTCHA (ação PAYMENT). Sem token, a API
        // recusaria — melhor avisar aqui do que deixar o cliente achar que o
        // cartão foi rejeitado.
        const recaptchaToken = await executeRecaptcha();
        if (!recaptchaToken) {
          throw new Error(t.creditCard.errors.recaptchaFailed);
        }

        // Passo 2 — recarga com o token do cartão, pela rota que aceita CARTAO
        // para qualquer marca.
        const recharge = await requests.rechargeWithCard({
          metodo_pagamento: 'CARTAO',
          sms: 'N',
          token: cardToken,
          ip: clientIp ?? undefined,
          numeros: [{ numero: session.linha, id_plano: guard.plan.id }],
          linha_token: session.linhaToken,
          recaptcha_token: recaptchaToken,
          // A API espera o id do plano a renovar todo mês, não um booleano.
          recarga_automatica_cartao: recurrence ? guard.plan.id : undefined,
        });

        if (recharge?.retorno === false) {
          throw new Error(recharge.mensagem || t.creditCard.errors.failed);
        }

        show({
          kind: 'success',
          title: t.creditCard.approvedTitle,
          text: t.creditCard.approvedBody,
          confirmLabel: t.creditCard.backHome,
          blocking: true,
          onConfirm: () => {
            // A recarga foi consumida: a sessão da linha não serve mais e o
            // cliente volta para a home, como no legado.
            clearSession();
            navigate(ROUTES.home);
          },
        });
      } catch (error) {
        // Mensagem da API quando existe; nunca o texto cru do axios
        // ("Network Error"/timeout), que não diz nada ao cliente.
        const text =
          error instanceof ApiError
            ? extractApiMessage(error.body, t.creditCard.errors.generic)
            : error instanceof Error && error.message
              ? error.message
              : t.creditCard.errors.generic;

        show({ kind: 'error', title: t.common.error, text });
      } finally {
        setSubmitting(false);
      }
    },
    [
      guard,
      submitting,
      session,
      holder,
      cardNumber,
      expiry,
      cvv,
      recurrence,
      cardPaymentAvailable,
      show,
      clearSession,
      navigate,
      t,
    ]
  );

  // Qualquer guarda reprovada devolve o cliente para a home, sem deixar
  // entrada no histórico (o legado fazia `redirect($redirectRoot)`).
  if (!guard.ok) {
    return <Navigate to={ROUTES.home} replace />;
  }

  return (
    <Box component="main" sx={{ flexGrow: 1, py: { xs: 4, sm: 6 }, px: { xs: 2, sm: 3 } }}>
      <Box
        sx={{
          maxWidth: 768,
          mx: 'auto',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
          gap: 3,
          alignItems: 'start',
        }}
      >
        <Box sx={{ gridColumn: { md: 'span 1' } }}>
          <OrderSummary
            plan={guard.plan}
            line={session?.linha ?? ''}
            holderDocument={guard.holderDocument}
          />
        </Box>

        <Box
          component="form"
          onSubmit={handleSubmit}
          noValidate
          autoComplete="off"
          sx={{ ...cardSx, gridColumn: { md: 'span 2' }, overflow: 'hidden' }}
        >
          <Box sx={{ px: { xs: 2, sm: 3 }, py: 3, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography
              component="h2"
              sx={{
                fontSize: '1.125rem',
                fontWeight: 700,
                color: colors.textPrimary,
                borderBottom: `1px solid ${colors.borderSoft}`,
                pb: 1,
                mb: 1,
              }}
            >
              {t.creditCard.formTitle}
            </Typography>

            <TextField
              id="card_name"
              fullWidth
              required
              type="text"
              autoComplete="cc-name"
              label={t.creditCard.nameLabel}
              placeholder={t.creditCard.namePlaceholder}
              value={holder}
              onChange={(event) => {
                // Caixa alta no próprio estado: é como o nome vai para a API.
                setHolder(event.target.value.toUpperCase());
                setIssue(null);
              }}
              error={Boolean(errorFor('name'))}
              helperText={errorFor('name') ?? ' '}
              slotProps={{ htmlInput: { maxLength: 60, autoCapitalize: 'characters' } }}
              sx={{ '& .MuiInputBase-input': { textTransform: 'uppercase' } }}
            />

            <TextField
              id="card_number"
              fullWidth
              required
              type="tel"
              autoComplete="cc-number"
              label={t.creditCard.numberLabel}
              placeholder={t.creditCard.numberPlaceholder}
              value={cardNumber}
              onChange={(event) => {
                setCardNumber(formatCardNumber(event.target.value));
                setIssue(null);
              }}
              error={Boolean(errorFor('number'))}
              helperText={errorFor('number') ?? ' '}
              slotProps={{
                htmlInput: { maxLength: 23, inputMode: 'numeric' },
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      {brand.label ? (
                        <Typography
                          component="span"
                          aria-label={t.creditCard.brandIconAlt}
                          sx={{
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            color: colors.textSecondary,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {brand.label}
                        </Typography>
                      ) : (
                        <CreditCardRoundedIcon
                          aria-label={t.creditCard.brandIconAlt}
                          sx={{ color: colors.textMuted }}
                        />
                      )}
                    </InputAdornment>
                  ),
                },
              }}
            />

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                gap: 2,
              }}
            >
              <TextField
                id="expiry"
                fullWidth
                required
                type="tel"
                autoComplete="cc-exp"
                label={t.creditCard.expiryLabel}
                placeholder={t.creditCard.expiryPlaceholder}
                value={expiry}
                onChange={(event) => {
                  setExpiry(formatExpiry(event.target.value));
                  setIssue(null);
                }}
                error={Boolean(errorFor('expiry'))}
                helperText={errorFor('expiry') ?? ' '}
                slotProps={{ htmlInput: { maxLength: 7, inputMode: 'numeric' } }}
              />

              <TextField
                id="cvv"
                fullWidth
                required
                type="tel"
                autoComplete="cc-csc"
                label={t.creditCard.cvvLabel}
                placeholder={t.creditCard.cvvPlaceholder}
                value={cvv}
                onChange={(event) => {
                  setCvv(formatCvv(event.target.value, brand));
                  setIssue(null);
                }}
                error={Boolean(errorFor('cvv'))}
                helperText={errorFor('cvv') ?? ' '}
                slotProps={{ htmlInput: { maxLength: brand.cvvLength, inputMode: 'numeric' } }}
              />
            </Box>

            <FormControlLabel
              sx={{ alignItems: 'flex-start', m: 0 }}
              control={
                <Checkbox
                  id="recurrence_check"
                  checked={recurrence}
                  onChange={(event) => setRecurrence(event.target.checked)}
                  sx={{ pt: 0, color: colors.border, '&.Mui-checked': { color: colors.primary } }}
                />
              }
              label={
                <Box sx={{ pt: 0.25 }}>
                  <Typography
                    sx={{ fontSize: '0.875rem', fontWeight: 600, color: colors.textPrimary }}
                  >
                    {t.creditCard.recurrenceLabel}
                  </Typography>
                  <Typography sx={{ fontSize: '0.875rem', color: colors.textSecondary }}>
                    {t.creditCard.recurrenceHint}
                  </Typography>
                </Box>
              }
            />
          </Box>

          <Box
            sx={{
              px: { xs: 2, sm: 3 },
              py: 2,
              backgroundColor: colors.surfaceMuted,
              borderTop: `1px solid ${colors.borderSoft}`,
              display: 'flex',
              justifyContent: { xs: 'stretch', md: 'flex-end' },
            }}
          >
            <Button
              type="submit"
              variant="contained"
              disabled={submitting || !cardPaymentAvailable}
              startIcon={
                submitting ? <CircularProgress size={18} sx={{ color: colors.white }} /> : undefined
              }
              sx={{
                ...primaryButtonSx,
                borderRadius: radius.sm,
                py: 1.5,
                px: 4,
                width: { xs: '100%', md: 'auto' },
              }}
            >
              {submitting ? t.creditCard.submitting : t.creditCard.submit}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
